import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, FileText } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { formatFileSize } from '../support/constants';
import { IconaAllegato, TargaTipoAllegato } from './IconaAllegato';
import { formatDataCircolare } from './ElencoCircolari';
import { etichettaCategoria, tipoAllegato } from '../../utils/circolariEtichette';
import {
  SETTORI_VISIBILITA,
  CircolareAllegato,
  CircolareDetail,
  CircolariApi,
  formatNumeroCircolare,
} from '../../services/circolariService';

/**
 * Scheda di una circolare con nota (ripulita lato client) e allegati aperti
 * tramite le chiamate autenticate di `api`. Condivisa tra area riservata e area
 * associativa. `onErroreAccesso` restituisce true se ha gestito l'errore.
 */
export interface DettaglioCircolareProps {
  api: CircolariApi;
  id?: string;
  /** Link dell'elenco per il bottone dello stato «non trovata» */
  linkElenco: string;
  /** Testo del bottone dello stato «non trovata» (es. «Torna a Comunicati e norme») */
  etichettaRitorno: string;
  /** Spiegazione dello stato «non trovata» */
  testoNonTrovata?: string;
  onErroreAccesso?: (error: any) => boolean;
}

const CARD = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';

const etichettaSettore = (value: string): string =>
  SETTORI_VISIBILITA.find((c) => c.value === value)?.label || value;

const DettaglioCircolare: React.FC<DettaglioCircolareProps> = ({
  api,
  id,
  linkElenco,
  etichettaRitorno,
  testoNonTrovata = 'La circolare non esiste più oppure non è disponibile.',
  onErroreAccesso,
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  const [circolare, setCircolare] = useState<CircolareDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [apertura, setApertura] = useState<string | null>(null);

  const gestisciAccesso = (error: any): boolean => !!onErroreAccesso && onErroreAccesso(error);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    api.getCircolare(id)
      .then((data) => { if (!cancelled) setCircolare(data); })
      .catch((error) => {
        if (cancelled) return;
        // 404 (inesistente o non visibile): basta lo stato «non trovata».
        if (!gestisciAccesso(error) && error?.response?.status !== 404) {
          toast.error('Errore nel caricamento della circolare');
        }
        setCircolare(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, id]);

  const handleOpen = async (allegato: CircolareAllegato) => {
    if (!circolare) return;
    setApertura(allegato.filename);
    try {
      // PDF: si apre in una nuova scheda. Word ed Excel: si scaricano col nome originale.
      if (tipoAllegato(allegato).azione === 'apri') await api.openAllegatoCircolare(circolare._id, allegato);
      else await api.scaricaAllegatoCircolare(circolare._id, allegato);
    } catch (error) {
      if (!gestisciAccesso(error)) toast.error('Impossibile aprire l\'allegato. Riprova tra qualche istante.');
    } finally {
      setApertura(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" aria-label="Caricamento">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!circolare) {
    return (
      <div className={`${CARD} text-center py-16 px-4`}>
        <FileText className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Circolare non trovata</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{testoNonTrovata}</p>
        <button
          type="button"
          onClick={() => navigate(linkElenco)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {etichettaRitorno}
        </button>
      </div>
    );
  }

  const nota = sanitizeHtml(circolare.nota || '');
  const allegati = circolare.allegati || [];
  const categorie =
    circolare.visibilita?.tipo === 'categorie' ? circolare.visibilita.categorie || [] : [];

  return (
    <article className={`${CARD} p-5 sm:p-6`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-indigo-600 dark:text-indigo-400">
          Circolare {formatNumeroCircolare(circolare)}
        </span>
        {circolare.dataCircolare && <span>del {formatDataCircolare(circolare.dataCircolare)}</span>}
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
          {etichettaCategoria(circolare.categoria)}
        </span>
        {categorie.map((c) => (
          <span
            key={c}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
          >
            {etichettaSettore(c)}
          </span>
        ))}
        {circolare.visibilita?.soloReteCertificata && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-100 dark:bg-cyan-900/40 text-cyan-800 dark:text-cyan-300">
            Rete Certificata
          </span>
        )}
      </div>
      <h1 className="text-base font-semibold text-gray-900 dark:text-white mt-2">{circolare.oggetto}</h1>

      {nota.trim() && (
        <div
          className="testo-ricco mt-4 text-gray-800 dark:text-gray-200"
          data-testid="nota-circolare"
          dangerouslySetInnerHTML={{ __html: nota }}
        />
      )}

      {allegati.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            {allegati.length === 1 ? 'Allegato' : `Allegati (${allegati.length})`}
          </h2>
          <ul className="space-y-2">
            {allegati.map((a) => {
              const scarica = tipoAllegato(a).azione === 'scarica';
              const nome = a.originalName || a.filename;
              return (
                <li key={a.filename}>
                  <button
                    type="button"
                    onClick={() => handleOpen(a)}
                    disabled={apertura === a.filename}
                    aria-label={`${scarica ? 'Scarica' : 'Apri'} ${nome}`}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40 hover:border-indigo-300 dark:hover:border-indigo-600 text-left transition-colors disabled:opacity-60"
                  >
                    <IconaAllegato allegato={a} />
                    <span className="flex-1 min-w-0 truncate text-sm text-gray-800 dark:text-gray-200">{nome}</span>
                    <TargaTipoAllegato allegato={a} />
                    {!!a.size && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
                        {formatFileSize(a.size)}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 shrink-0">
                      {scarica
                        ? <Download className="w-3.5 h-3.5" aria-hidden="true" />
                        : <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />}
                      {apertura === a.filename ? (scarica ? 'Download...' : 'Apertura...') : (scarica ? 'Scarica' : 'Apri')}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </article>
  );
};

export default DettaglioCircolare;
